/* ==========================================================================
 * resale-form.js — /pages/revendre-mon-mobilier
 *
 * 2-step form. Step 1 validates locally, stores data in sessionStorage, and
 * writes the lead to HubSpot (Forms API). Step 2 triggers the Shopify Forms
 * widget (photo/inventory upload → metaobject).
 *
 * No bundlers, no imports — vanilla JS IIFE, idempotent.
 * ========================================================================== */

(function () {
  'use strict';

  var SESSION_KEY = 'redesk.resale.session';
  var SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24h
  var BOUND_FLAG = '__resaleFormBound';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Strip spaces/dots/hyphens before testing
  var PHONE_RE = /^(\+33|0)[1-9](\d{8})$/;

  var FALLBACK_I18N = {
    'resale.error.required': 'Ce champ est requis.',
    'resale.error.email': 'Adresse e-mail invalide.',
    'resale.error.phone': 'Numéro de téléphone invalide (ex. 06 12 34 56 78 ou +33 6 12 34 56 78).',
    'resale.error.consent': 'Vous devez accepter pour continuer.',
    'resale.error.pro_certify': 'Vous devez certifier être un professionnel pour continuer.',
    'resale.error.network': 'Une erreur réseau est survenue. Merci de réessayer dans un instant.',
    'resale.error.invalid_data': 'Certaines informations sont invalides.'
  };

  function initAll() {
    var nodes = document.querySelectorAll('[data-resale-form]');
    for (var i = 0; i < nodes.length; i++) {
      var root = nodes[i];
      if (root[BOUND_FLAG]) continue;
      root[BOUND_FLAG] = true;
      try { new ResaleForm(root); }
      catch (e) { console.error('[resale-form] init failed', e); }
    }
  }

  // ---- Class ---------------------------------------------------------------
  function ResaleForm(root) {
    this.root = root;
    this.form1 = root.querySelector('form[data-resale-step="1"]');
    this.form2 = root.querySelector('form[data-resale-step="2"]');
    this.errorEl = root.querySelector('[data-resale-error]');
    this.progressBar = root.querySelector('[data-resale-progress]');
    this.stepLabels = root.querySelectorAll('[data-resale-step-label]');

    this.i18n = readI18n();
    this.state = 'step1';
    this.session = readSession();
    this.profile = 'professionnel';
    this.step1Data = null;

    this.bind();
    this.setupShopifyFormsWidget();
    this.updateProfileUI(); // apply initial particulier state
    this.resumeIfApplicable();
    this.observeStepView(this.form1, 'resale_form_step_1_viewed');
  }

  ResaleForm.prototype.bind = function () {
    var self = this;
    if (this.form1) {
      this.form1.addEventListener('submit', function (e) {
        e.preventDefault();
        self.handleStep1Submit();
      });
      this.form1.addEventListener('input', function (e) {
        self.clearFieldError(e.target);
      });
      // Profile toggle — direct binding on each button (most reliable)
      var profileBtns = this.form1.querySelectorAll('[data-resale-profile-btn]');
      for (var pi = 0; pi < profileBtns.length; pi++) {
        (function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            self.profile = btn.getAttribute('data-resale-profile-btn');
            self.updateProfileUI();
          });
        })(profileBtns[pi]);
      }
    }
    if (this.form2) {
      // No submit handler: the native Shopify Forms widget owns Step 2
      // submission. The theme only wires the "Retour" button.
      this.form2.addEventListener('input', function (e) {
        self.clearFieldError(e.target);
      });
      var back = this.form2.querySelector('[data-resale-back]');
      if (back) {
        back.addEventListener('click', function () { self.goToStep1(); });
      }
    }
  };

  // ---- Profile toggle ------------------------------------------------------
  ResaleForm.prototype.updateProfileUI = function () {
    if (!this.form1) return;
    var btns = this.form1.querySelectorAll('[data-resale-profile-btn]');
    for (var i = 0; i < btns.length; i++) {
      var active = btns[i].getAttribute('data-resale-profile-btn') === this.profile;
      btns[i].classList.toggle('resale-profile-toggle__btn--active', active);
      btns[i].setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    // Show / hide company field and toggle its required attribute
    var companyWrap = this.form1.querySelector('[data-profile-show="professionnel"]');
    if (companyWrap) {
      var isPro = this.profile === 'professionnel';
      if (isPro) {
        companyWrap.removeAttribute('hidden');
      } else {
        companyWrap.setAttribute('hidden', '');
        // Clear value so it's not sent when hidden
        var input = companyWrap.querySelector('input, select, textarea');
        if (input) input.value = '';
      }
      var companyInput = companyWrap.querySelector('input, select, textarea');
      if (companyInput) {
        if (isPro) {
          companyInput.setAttribute('required', '');
          companyInput.setAttribute('aria-required', 'true');
        } else {
          companyInput.removeAttribute('required');
          companyInput.removeAttribute('aria-required');
        }
      }
    }

    // Sync hidden profile input
    var profileInput = this.form1.querySelector('[data-resale-profile-value]');
    if (profileInput) profileInput.value = this.profile;
  };

  // ---- Stepper -------------------------------------------------------------
  ResaleForm.prototype.updateStepper = function (step) {
    if (this.progressBar) {
      this.progressBar.style.width = step === 2 ? '100%' : '50%';
    }
    if (this.stepLabels) {
      for (var i = 0; i < this.stepLabels.length; i++) {
        var labelStep = parseInt(this.stepLabels[i].getAttribute('data-resale-step-label'), 10);
        this.stepLabels[i].classList.toggle('resale-form-stepper__label--active', labelStep === step);
      }
    }
  };

  // ---- State transitions ---------------------------------------------------
  ResaleForm.prototype.goToStep2 = function () {
    if (!this.form1 || !this.form2) return;
    // Fire-and-forget second chance at HubSpot capture if Step 1 failed. The
    // native Shopify Forms widget now owns Step 2 submission, so this is the
    // last theme-side hook where we can retry the lead capture. Never throws.
    try { this.retryFailedSignups(); } catch (_) {}
    this.state = 'step2';
    this.form1.setAttribute('hidden', '');
    this.form2.removeAttribute('hidden');
    this.updateStepper(2);
    this.fireEvent('resale_form_step_2_viewed');
    // On a fresh flow the email is only known once Step 1 is submitted, so the
    // sync in setupShopifyFormsWidget (which fires at load) may have run before
    // step1Data existed. Re-sync here, where step1Data is guaranteed populated;
    // syncContactIntoShopifyForm is idempotent and no-ops if the widget isn't
    // mounted yet. The resume path is covered by setupShopifyFormsWidget.
    var step1 = (this.session && this.session.step1Data) || this.step1Data;
    try { syncContactIntoShopifyForm(this.form2, step1); } catch (_) {}
    var firstField = this.form2.querySelector('input, select, textarea');
    if (firstField) try { firstField.focus(); } catch (_) {}
  };

  ResaleForm.prototype.goToStep1 = function () {
    if (!this.form1 || !this.form2) return;
    // Clicking "Retour" from Step 2 means the user wants to start over: wipe
    // the session so a reload lands them on a fresh Step 1.
    clearSession();
    this.session = null;
    this.state = 'step1';
    this.form2.setAttribute('hidden', '');
    this.form1.removeAttribute('hidden');
    this.updateStepper(1);
  };

  ResaleForm.prototype.showError = function (key) {
    if (!this.errorEl) return;
    this.errorEl.textContent = this.t(key || 'resale.error.network');
    this.errorEl.removeAttribute('hidden');
  };

  ResaleForm.prototype.hideError = function () {
    if (this.errorEl) this.errorEl.setAttribute('hidden', '');
  };

  // ---- Step 1 --------------------------------------------------------------
  // Validate, persist, then write the lead to HubSpot (public Forms API).
  // hubspotStep1Capture never throws — returns a status object. The Step 2
  // transition fires unconditionally after it settles: lead capture > perfect
  // sync. Failures are retried at Step 2 submit via retryFailedSignups.
  ResaleForm.prototype.handleStep1Submit = function () {
    if (this.state === 'step1-submitting') return;

    var values = collectFormValues(this.form1);
    var errors = validateStep1(values, this.form1);
    if (Object.keys(errors).length) {
      this.applyFieldErrors(this.form1, errors);
      return;
    }
    this.hideError();

    var self = this;
    this.step1Data = values;

    // Preserve any prior signup substate so a re-submit (same tab) can
    // short-circuit a HubSpot capture that already succeeded — see idempotency
    // block in hubspotStep1Capture below.
    var priorSignup = (this.session && this.session.signup) || null;
    this.session = {
      email: values.email,
      step1Data: values,
      step1SubmittedAt: Date.now(),
      signup: priorSignup || {
        hubspot: { status: 'pending' }
      }
    };
    writeSession(this.session);

    this.state = 'step1-submitting';
    setSubmitting(this.form1, true);

    var hubspotP = hubspotStep1Capture(this.root, values, this.session);

    hubspotP.then(function (hubspotOut) {
      // hubspotStep1Capture never rejects, but guard for future edits.
      if (!hubspotOut || typeof hubspotOut !== 'object') {
        hubspotOut = { status: 'failed', error: 'unexpected' };
      }

      if (!self.session) {
        self.session = {
          email: values.email,
          step1Data: values,
          step1SubmittedAt: Date.now(),
          signup: { hubspot: { status: 'pending' } }
        };
      }
      self.session.signup = { hubspot: hubspotOut };
      writeSession(self.session);

      // Event fires only on real success — 'skipped' (HubSpot not configured)
      // intentionally does NOT fire so analytics doesn't see phantom captures.
      if (hubspotOut.status === 'ok') {
        var portalId = self.root && self.root.getAttribute('data-portal-id');
        var formGuid = self.root && self.root.getAttribute('data-form-id-step-1');
        self.fireEvent('resale_form_step_1_hubspot_captured', {
          email: values.email,
          portalId: portalId || null,
          formGuid: formGuid || null
        });
      }

      // Umbrella event — reuses the existing event name so any analytics
      // already wired keeps firing.
      self.fireEvent('resale_form_step_1_submitted', {
        email: values.email,
        hubspot: hubspotOut.status
      });

      self.state = 'step1';
      setSubmitting(self.form1, false);
      self.goToStep2();
    }).catch(function (err) {
      // HubSpot capture is best-effort and non-blocking: even if the promise
      // rejects (future edit, exception in writeSession, etc.), the user must
      // still reach Step 2 — never strand the form in 'step1-submitting'
      // (infinite spinner). Record the failure and advance regardless.
      console.warn('[resale-form] hubspot step1 capture failed (non-blocking)', err);
      try {
        if (self.session) {
          self.session.signup = { hubspot: { status: 'failed', error: String((err && err.message) || err || 'unknown') } };
          writeSession(self.session);
        }
      } catch (_) {}
      self.state = 'step1';
      setSubmitting(self.form1, false);
      self.goToStep2();
    });
  };

  // ---- Step 2 --------------------------------------------------------------
  // Step 2 submission is owned entirely by the native Shopify Forms widget: its
  // own submit button posts the furniture/reason/upload fields → metaobject, and
  // shows its own confirmation. The theme no longer drives this — see
  // setupShopifyFormsWidget, which pre-fills the hidden email at render time.

  // The Shopify Forms widget renders inside the OPEN shadow root of
  // <shopify-forms-embed>. Theme CSS and light-DOM querySelector can't reach
  // it, so everything below pierces host.shadowRoot.
  function getShopifyFormsHost(parentForm) {
    return parentForm ? parentForm.querySelector('shopify-forms-embed') : null;
  }
  function getShopifyFormsRoot(parentForm) {
    var host = getShopifyFormsHost(parentForm);
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  // Shopify Forms app blocks only render reliably when output by the section
  // (not a snippet), so the widget is rendered as a sibling of the Step 2 form
  // in resale-hero.liquid. Move it into the in-form slot so it follows Step 2
  // visibility, then inject a scoped <style> into the shadow root to hide the
  // duplicate email field (pre-filled from Step 1). The widget's OWN submit
  // button stays visible — it now drives Step 2 submission. Once the shadow
  // root + email field are ready, pre-fill the hidden email from the Step 1
  // session so the native submission carries the contact.
  ResaleForm.prototype.setupShopifyFormsWidget = function () {
    if (!this.form2) return;
    var widget = this.root.querySelector('[data-resale-shopify-forms]');
    var slot = this.form2.querySelector('[data-resale-shopify-forms-slot]');
    if (widget && slot && !slot.contains(widget)) {
      slot.appendChild(widget);
    }
    if (widget) widget.removeAttribute('hidden');

    // The app creates <shopify-forms-embed> AND its shadow root asynchronously,
    // so neither may exist at init. Poll for both before injecting the masking
    // style (persists across the component's reconnect since the shadow root is
    // bound to the element) and pre-filling the email. Query from the card root
    // so it's found wherever the app mounts it.
    var self = this;
    var root = this.root;
    var attempts = 0;
    function setup() {
      var host = root.querySelector('shopify-forms-embed');
      var sr = host && host.shadowRoot;
      var emailInput = sr && sr.querySelector('[data-testid="field-email"]');
      if (!sr || !emailInput) {
        if (attempts++ < 100) setTimeout(setup, 100);
        return;
      }
      if (!sr.querySelector('style[data-resale-injected]')) {
        var style = document.createElement('style');
        style.setAttribute('data-resale-injected', '');
        style.textContent =
          /* Email + téléphone : remplis en JS depuis l'étape 1 → masqués
             (visually-hidden pour rester "mounted" côté React). Le téléphone est
             un champ texte custom (field-custom#tlphone) ; on cible son conteneur
             direct ET son _formFieldContainer_ selon la profondeur du markup. */
          'div:has(> [data-testid="field-email"]),div:has(> [data-testid="field-custom#tlphone"]),div[class*="_formFieldContainer_"]:has([data-testid="field-custom#tlphone"]){position:absolute!important;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}';
        sr.appendChild(style);
      }
      // Pre-fill the hidden email from the Step 1 session. session.step1Data
      // carries the contact; fall back to the session itself for older shapes.
      var step1 = (self.session && self.session.step1Data) || self.session || self.step1Data;
      syncContactIntoShopifyForm(self.form2, step1);
    }
    setup();
  };

  // Set a value on a React-controlled input via the native setter so the
  // widget's internal state picks it up (a plain `.value =` is overwritten on
  // the next React render).
  function setNativeValue(el, value) {
    var proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    var desc = Object.getOwnPropertyDescriptor(proto, 'value');
    if (desc && desc.set) {
      desc.set.call(el, value);
    } else {
      el.value = value;
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Sync the contact fields (email + phone) from Step 1 into the widget's
  // hidden fields. Both are plain text inputs, so a single native-setter write
  // sticks. The phone lives in a custom short-text field (field-custom#tlphone)
  // deliberately chosen over the app's "Phone" field type, whose intl-tel
  // component ignores programmatic writes. All other fields (furniture, reason,
  // uploads) are entered directly in the widget by the user. Each field degrades
  // gracefully (querySelector null → skipped) if absent from the Shopify Form.
  function syncContactIntoShopifyForm(parentForm, step1) {
    var sr = getShopifyFormsRoot(parentForm);
    if (!sr || !step1) return;
    var fields = { 'field-email': step1.email, 'field-custom#tlphone': step1.phone };
    Object.keys(fields).forEach(function (testid) {
      var value = fields[testid];
      if (!value) return;
      var input = sr.querySelector('[data-testid="' + testid + '"]');
      if (input) setNativeValue(input, value);
    });
  }

  // ---- Resume --------------------------------------------------------------
  ResaleForm.prototype.resumeIfApplicable = function () {
    if (!this.session) return;
    // Embeds that opt out (e.g. on /pages/contact) always start at Step 1 and
    // discard any sessionStorage left over from a previous /pages/revendre-mon-mobilier
    // visit, otherwise the user lands on Step 2 with stale Step 1 data.
    if (this.root.hasAttribute('data-resale-no-resume')) {
      clearSession();
      this.session = null;
      return;
    }
    var fresh = (Date.now() - (this.session.step1SubmittedAt || 0)) < SESSION_TTL_MS;
    if (!fresh) {
      clearSession();
      this.session = null;
      return;
    }
    this.step1Data = this.session.step1Data || null;
    this.goToStep2();
  };

  // ---- Validation ----------------------------------------------------------
  function validateStep1(values, formEl) {
    var errors = {};
    requiredCheck(formEl, values, errors);
    if (values.email && !EMAIL_RE.test(values.email)) {
      errors.email = 'resale.error.email';
    }
    if (values.phone) {
      var stripped = String(values.phone).replace(/[\s.\-]/g, '');
      if (!PHONE_RE.test(stripped)) errors.phone = 'resale.error.phone';
    }
    if (formEl) {
      var consentEl = formEl.querySelector('input[name="consent"]');
      if (consentEl && consentEl.required && !consentEl.checked) {
        errors.consent = 'resale.error.consent';
      }
      var proCertifyEl = formEl.querySelector('input[name="pro_certify"]');
      if (proCertifyEl && proCertifyEl.required && !proCertifyEl.checked) {
        errors.pro_certify = 'resale.error.pro_certify';
      }
    }
    return errors;
  }

  function requiredCheck(formEl, values, errors) {
    if (!formEl) return;
    var fields = formEl.querySelectorAll('[required]');
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (!f.name) continue;
      // Skip fields inside a hidden ancestor
      if (f.closest('[hidden]')) continue;
      var bare = f.name.replace(/\[\]$/, '');
      if (errors[bare]) continue;
      var v = values[bare];
      var empty = (v === undefined || v === null || v === '' ||
        (Array.isArray(v) && v.length === 0));
      if (empty) errors[bare] = 'resale.error.required';
    }
  }

  ResaleForm.prototype.applyFieldErrors = function (formEl, errors) {
    var firstInvalid = null;
    var keys = Object.keys(errors);
    for (var i = 0; i < keys.length; i++) {
      var name = keys[i];
      var key = errors[name];
      var wrapper = formEl.querySelector(
        '.resale-field[data-field="' + name + '"], .resale-fieldset[data-field="' + name + '"]'
      );
      var msgEl = wrapper && wrapper.querySelector('.resale-field__error');
      var inputEl = formEl.querySelector('[name="' + name + '"], [name="' + name + '[]"]');
      if (msgEl) msgEl.textContent = this.t(key);
      if (inputEl) {
        inputEl.setAttribute('aria-invalid', 'true');
        if (!firstInvalid) firstInvalid = inputEl;
      }
    }
    if (firstInvalid) try { firstInvalid.focus(); } catch (_) {}
  };

  ResaleForm.prototype.clearFieldError = function (target) {
    if (!target || !target.name) return;
    var name = target.name.replace(/\[\]$/, '');
    var wrapper = target.closest &&
      target.closest('.resale-field[data-field="' + name + '"], .resale-fieldset[data-field="' + name + '"]');
    if (wrapper) {
      var msgEl = wrapper.querySelector('.resale-field__error');
      if (msgEl) msgEl.textContent = '';
    }
    target.removeAttribute('aria-invalid');
  };

  // ---- Step 1 lead capture: HubSpot (public Forms API) --------------------
  // Feature-gated: when data-portal-id and data-form-id-step-1 are blank on
  // [data-resale-form] (i.e. operator hasn't configured HubSpot yet), the call
  // is dormant and returns 'skipped'. The failure matrix treats 'skipped' the
  // same as 'ok' — does not block, does not retry, does not surface a banner.
  // This is what lets the user see Shopify-side working before wiring HubSpot.
  // NEVER throws.
  function hubspotStep1Capture(root, values, session) {
    if (session && session.signup && session.signup.hubspot && session.signup.hubspot.status === 'ok') {
      return Promise.resolve(session.signup.hubspot);
    }
    var portalId = root && root.getAttribute('data-portal-id');
    var formGuid = root && root.getAttribute('data-form-id-step-1');
    if (!portalId || !formGuid) {
      // HubSpot not configured — record as 'skipped' (not 'failed'); does not
      // count against retry, does not block Step 2.
      return Promise.resolve({ status: 'skipped' });
    }

    // Best-effort hubspotutk lookup for cross-domain attribution.
    var hutk = '';
    try {
      var match = document.cookie.match(/(?:^|; )hubspotutk=([^;]+)/);
      if (match) hutk = decodeURIComponent(match[1]);
    } catch (_) {}

    var fields = [
      { name: 'email', value: values.email || '' },
      { name: 'firstname', value: values.fullname || '' },
      { name: 'phone', value: values.phone || '' },
      { name: 'company', value: values.company || '' },
      { name: 'lifecyclestage', value: 'lead' }
    ];
    var context = {
      pageUri: location.href,
      pageName: document.title
    };
    if (hutk) context.hutk = hutk;

    var consentText = '';
    try {
      var consentEl = document.querySelector('input[name="consent"]');
      var consentLabel = consentEl && consentEl.closest('label');
      if (consentLabel) consentText = (consentLabel.textContent || '').trim();
    } catch (_) {}

    var body = { fields: fields, context: context };
    if (values.consent === true) {
      // Per orchestrator decision: send consentToProcess + text only, no
      // subscriptionTypeId (deferred — portal-specific value not available).
      body.legalConsentOptions = {
        consent: {
          consentToProcess: true,
          text: consentText || 'Consent to process'
        }
      };
    }

    var url = 'https://api.hsforms.com/submissions/v3/integration/submit/' +
      encodeURIComponent(portalId) + '/' + encodeURIComponent(formGuid);

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (r.status === 200) return { status: 'ok' };
      // Read body for context but don't choke on non-JSON.
      return r.text().then(function (txt) {
        return { status: 'failed', error: 'HubSpot ' + r.status + (txt ? (' — ' + txt.slice(0, 200)) : '') };
      }, function () {
        return { status: 'failed', error: 'HubSpot ' + r.status };
      });
    }, function (err) {
      return { status: 'failed', error: String((err && err.message) || err || 'network') };
    });
  }

  // ---- Step 2 retry hook (fire-and-forget from goToStep2) ------------------
  // Retries the HubSpot side if it failed at Step 1. 'skipped' (not configured)
  // and 'ok' are left alone. Updates session.signup in place and writes back.
  // Always resolves — never rejects — so the caller can safely .then() ahead.
  ResaleForm.prototype.retryFailedSignups = function () {
    var self = this;
    if (!this.session || !this.session.signup) return Promise.resolve();
    var values = this.step1Data || (this.session && this.session.step1Data) || {};
    var pending = [];

    if (this.session.signup.hubspot && this.session.signup.hubspot.status === 'failed') {
      pending.push(
        hubspotStep1Capture(this.root, values, null)
          .then(function (out) { self.session.signup.hubspot = out; })
      );
    }
    if (!pending.length) return Promise.resolve();

    return Promise.all(pending).then(function () {
      writeSession(self.session);
    }, function () {
      // Helpers don't throw, but defend anyway.
      writeSession(self.session);
    });
  };

  // ---- Form data collection ------------------------------------------------
  function collectFormValues(formEl) {
    var out = {};
    if (!formEl) return out;
    var fd = new FormData(formEl);
    fd.forEach(function (val, key) {
      // Files: keep only the filename for the URL-encoded recap email path.
      // (Shopify /contact endpoint can't store binary attachments — this at
      // least surfaces the filename so the team can ask the visitor to forward.)
      var stored = (val && typeof val === 'object' && 'name' in val && 'size' in val)
        ? (val.size > 0 ? val.name : '')
        : val;
      if (key.endsWith('[]')) {
        var bare = key.slice(0, -2);
        if (!Array.isArray(out[bare])) out[bare] = [];
        if (stored) out[bare].push(stored);
      } else if (stored !== '') {
        out[key] = stored;
      }
    });
    // Coerce consent checkbox to boolean
    var consent = formEl.querySelector('input[name="consent"]');
    if (consent) out.consent = !!consent.checked;
    return out;
  }

  // ---- Analytics events ----------------------------------------------------
  // Funnel/intermediate signals only — no PII at top-level. The native Shopify
  // Forms widget now owns Step 2 submission, so the theme no longer fires a
  // terminal `redesk_form_submit` event here.
  ResaleForm.prototype.fireEvent = function (name, extra) {
    var payload = {
      event: name,
      timestamp: new Date().toISOString(),
      page: location.pathname
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    try {
      var detail = extra ? Object.assign({}, payload, extra) : payload;
      document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (_) {}
  };

  ResaleForm.prototype.observeStepView = function (formEl, eventName) {
    if (!formEl) return;
    var self = this;
    if (typeof IntersectionObserver === 'undefined') {
      if (!formEl.hasAttribute('hidden')) self.fireEvent(eventName);
      return;
    }
    var obs = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          self.fireEvent(eventName);
          obs.disconnect();
          break;
        }
      }
    }, { threshold: 0.25 });
    obs.observe(formEl);
  };

  // ---- i18n ----------------------------------------------------------------
  ResaleForm.prototype.t = function (key) {
    if (this.i18n && Object.prototype.hasOwnProperty.call(this.i18n, key)) return this.i18n[key];
    if (Object.prototype.hasOwnProperty.call(FALLBACK_I18N, key)) return FALLBACK_I18N[key];
    return key;
  };

  function readI18n() {
    var node = document.getElementById('resale-i18n');
    if (!node) return null;
    try { return JSON.parse(node.textContent || '{}'); }
    catch (e) { return null; }
  }

  // ---- Session storage -----------------------------------------------------
  function readSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function writeSession(obj) {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(obj)); }
    catch (e) {}
  }

  function clearSession() {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
  }

  // ---- DOM helpers ---------------------------------------------------------
  function setSubmitting(formEl, on) {
    if (!formEl) return;
    var btn = formEl.querySelector('[type="submit"]');
    if (btn) {
      btn.disabled = !!on;
      btn.setAttribute('aria-busy', on ? 'true' : 'false');
    }
    formEl.setAttribute('data-submitting', on ? 'true' : 'false');
  }

  // ---- Boot — placed at the bottom so all prototype methods are assigned
  // before initAll() runs. With `defer`, readyState is 'interactive' here,
  // so the else branch runs synchronously.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
